import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router'
import { useEffect, type ReactNode } from 'react'
import { SessionProvider, useSession } from './session'
import { Layout } from './components/Layout'
import { Login, Register } from './pages/Auth'
import { Live } from './pages/Live'
import { LiveStatus } from './pages/LiveStatus'
import { AppsPage } from './pages/Apps'
import { DeviceLink, OAuthAuthorize } from './pages/AppAuth'
import { DevicesPage } from './pages/Devices'
import { ApiTokensPage } from './pages/ApiTokens'
import { BillingPage } from './pages/Billing'
import { AdminApiTokens } from './admin/ApiTokens'
import { AdminOverview } from './admin/Overview'
import { AdminUsers } from './admin/Users'
import { AdminUserCard } from './admin/users/Card'
import { AdminStreams } from './admin/Streams'
import { AdminOutputs } from './admin/Outputs'
import { AdminAudit } from './admin/Audit'
import { AdminStatusPage } from './admin/StatusPage'
import { AdminSettings } from './admin/Settings'
import { AdminLicense } from './admin/License'
import { RouteCardPage } from './admin/network/RouteCard'
import { NetworkNodesPage } from './admin/network/NodesList'
import { GroupPageRoute } from './admin/network/GroupPage'
import { NodeCardPage } from './admin/network/NodeCard'
import { RoutesListPage } from './admin/network/RoutesList'
import { MapPage } from './admin/network/MapTable'
import { CAPTIONS_PATH, NODES_PATH, nodePath } from './admin/network/api'
import { NetworkCaptionsPage } from './admin/network/CaptionsNodes'
import { NetworkTranscodersPage } from './admin/network/Transcoders'

function RequireUser({ admin, children }: { admin?: boolean; children: ReactNode }) {
  const { user, loading } = useSession()
  const loc = useLocation()
  if (loading) return <p className="p-8 text-sm text-muted">Loading…</p>
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />
  if (admin && user.role !== 'admin') return <Navigate to="/" replace />
  // Separate user and admin names (docs/deploy.md «Имена»): admin pages live
  // on the admin name, the cabinet's pages on the user name.
  const isAdminPath = loc.pathname === '/admin' || loc.pathname.startsWith('/admin/')
  if (isAdminPath && user.admin_url) return <External to={user.admin_url + loc.pathname + loc.search} />
  if (!isAdminPath && user.area === 'admin') {
    if (loc.pathname === '/') return <Navigate to="/admin" replace />
    if (user.app_url) return <External to={user.app_url + loc.pathname + loc.search} />
  }
  return <>{children}</>
}

/** A page of the other name: a full navigation, not a client route. */
function External({ to }: { to: string }) {
  useEffect(() => {
    location.replace(to)
  }, [to])
  return <p className="p-8 text-sm text-muted">Opening…</p>
}

/**
 * The old node addresses (before Network, R0.13b): /admin/nodes → Network › Nodes,
 * /admin/nodes?node=<id> (links from a route path) → that node's card.
 */
function OldNodes() {
  const [p] = useSearchParams()
  const id = p.get('node')
  if (id) return <Navigate to={nodePath(id)} replace />
  const rest = new URLSearchParams(p)
  return <Navigate to={`${NODES_PATH}${rest.toString() ? `?${rest}` : ''}`} replace />
}

export function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        {/* Signing an app in (docs/bridge-auth.md): full-page, no cabinet chrome. */}
        <Route path="/oauth/authorize" element={<RequireUser><OAuthAuthorize /></RequireUser>} />
        <Route path="/device" element={<RequireUser><DeviceLink /></RequireUser>} />
        <Route
          element={
            <RequireUser>
              <Layout />
            </RequireUser>
          }
        >
          <Route index element={<Live />} />
          {/* Live · Status: its own address (bookmark, a second screen); Studio | Status switch in the Live bar. */}
          <Route path="status" element={<LiveStatus />} />
          <Route path="apps" element={<AppsPage />} />
          <Route path="account/devices" element={<DevicesPage />} />
          <Route path="account/api-tokens" element={<ApiTokensPage />} />
          {/* Plan & usage (BILL-B5a): only when the center bills; the box redirects to the Live screen. */}
          <Route path="account/billing" element={<BillingPage />} />
          {/* Destinations are edited in their cards on the Live screen now. */}
          <Route path="destinations" element={<Navigate to="/" replace />} />
          <Route path="admin" element={<RequireUser admin><AdminOverview /></RequireUser>} />
          <Route path="admin/nodes" element={<RequireUser admin><OldNodes /></RequireUser>} />
          <Route path="admin/network" element={<Navigate to={NODES_PATH} replace />} />
          <Route path="admin/network/nodes" element={<RequireUser admin><NetworkNodesPage /></RequireUser>} />
          <Route path="admin/network/nodes/:id" element={<RequireUser admin><NodeCardPage /></RequireUser>} />
          <Route path="admin/network/groups/:id" element={<RequireUser admin><GroupPageRoute /></RequireUser>} />
          <Route path="admin/users" element={<RequireUser admin><AdminUsers /></RequireUser>} />
          <Route path="admin/users/:id" element={<RequireUser admin><AdminUserCard /></RequireUser>} />
          <Route path="admin/users/:id/billing" element={<RequireUser admin><AdminUserCard tab="plan" /></RequireUser>} />
          <Route path="admin/users/:id/:tab" element={<RequireUser admin><AdminUserCard /></RequireUser>} />
          <Route path="admin/streams" element={<RequireUser admin><AdminStreams /></RequireUser>} />
          {/* Routing network R0: the routes list and the route card (read-only), the map as a table. */}
          <Route path="admin/network/routes" element={<RequireUser admin><RoutesListPage /></RequireUser>} />
          <Route path="admin/network/routes/:id" element={<RequireUser admin><RouteCardPage /></RequireUser>} />
          <Route path="admin/network/map" element={<RequireUser admin><MapPage /></RequireUser>} />
          <Route path="admin/outputs" element={<RequireUser admin><AdminOutputs /></RequireUser>} />
          <Route path="admin/audit" element={<RequireUser admin><AdminAudit /></RequireUser>} />
          <Route path="admin/status" element={<RequireUser admin><AdminStatusPage /></RequireUser>} />
          <Route path="admin/settings" element={<RequireUser admin><AdminSettings /></RequireUser>} />
          <Route path="admin/settings/api-tokens" element={<RequireUser admin><AdminApiTokens /></RequireUser>} />
          <Route path="admin/license" element={<RequireUser admin><AdminLicense /></RequireUser>} />
          <Route path="admin/captions" element={<Navigate to={CAPTIONS_PATH} replace />} />
          <Route path="admin/network/captions" element={<RequireUser admin><NetworkCaptionsPage /></RequireUser>} />
          <Route path="admin/network/transcoders" element={<RequireUser admin><NetworkTranscodersPage /></RequireUser>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SessionProvider>
  )
}
